import { inject, Injectable, signal } from '@angular/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services';
import { PayloadToReplacePoint } from '../interfaces';
import dayjs from 'dayjs';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CustomRouteResponse, CustomRouteResponseCompleted } from 'src/app/pages/custom-route/interfaces/custom-route.response';
import { environment } from 'src/environments/environment';
import { CustomEditedRouteResponse } from 'src/app/pages/custom-route/shared/planner/components/edit-route/interfaces/edit-route.response';

@Injectable({
  providedIn: 'root'
})
export class UpdateCustomRouteData {
  private _http = inject(HttpClient);
  private _customRouteService = inject(CustomRouteService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  
  private _customRouteData = this._customRouteService.customRouteData;
  private _dayToShowIndex = this._plannerDayStateService.dayToShowIndex;
  
  private _isLoading = signal<boolean>(false);
  public isLoading = this._isLoading.asReadonly();
  
  setIsLoading(value: boolean): void {
    this._isLoading.set(value);
  }
  
  /**
   * Sends the new edited custom route data to the server
   * @param dayToShowIndex - The index of the day being edited
   * @returns Observable with the response
   */
  public sendNewEditedCustomRouteData(  
    pointToBeReplace: any,
    newPointToAdd: any
  ) : Observable<CustomEditedRouteResponse> {    
    const editedCustomEditedRouteBody = this.getPayloadToReplacePoint(pointToBeReplace, newPointToAdd);
    
    return this.updateCustomRouteDataDay(editedCustomEditedRouteBody)
      .pipe(
        map((response: CustomEditedRouteResponse) => {
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(`Error updating custom route data: ${error}`);
          return throwError(() => error);
        })
      );
  }
  
  updateCustomRouteDataDay(payload: PayloadToReplacePoint): Observable<CustomRouteResponse> {
    this._isLoading.set(true);
    
    const url = environment.apiUrlBase + '/api/replace-route-planning-day';
    return this._http.post<CustomRouteResponse>(url, payload).pipe(
      map((response: CustomRouteResponseCompleted) => {
        this._isLoading.set(false);
        console.log('Response from replace-route-planning-day:', response);
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this._isLoading.set(false);
        return throwError(() => new Error('HTTP request failed', { cause: error })); // Propagate the error
      })
    )
  }
    
  getPayloadToReplacePoint (
    pointToBeReplace: { id: number, type: string, title: string, startDate: string },
    newPointToAdd: { id: number, type: string, title: string, startDate: string }
  ): PayloadToReplacePoint
  {
    const customRouteData = this._customRouteData();
    if (!customRouteData) return;
    
    const dateOfTheDay = customRouteData.dailyRoutes[this._dayToShowIndex()]?.date;
    if (!dateOfTheDay) return;
    
    const dateString = dayjs(dateOfTheDay).format('YYYY-MM-DD');
    
    // build new points array replacing the point
    const dailyPoints = customRouteData.dailyRoutes[this._dayToShowIndex()]?.points;
    if (!dailyPoints) return;
    
    const points: any[]= dailyPoints.map(point => {
      if (point.id === pointToBeReplace.id && point.type === pointToBeReplace.type) {
        return {
          id: newPointToAdd.id,
          type: newPointToAdd.type,
          title: newPointToAdd.title,
          startDate: newPointToAdd.startDate
        };
      } else {
        return {
          id: point.id,
          type: point.type,
          title: point.title,
          startDate: point.startDate
        };
      }
    });
    
    const payload: PayloadToReplacePoint = {
      token: customRouteData.token,
      date: dateString,
      points: points
    };
    
    return payload;
  }
}
